import { memo, useCallback } from 'react';

import { type WorkspaceAgentGuidedVariantWatermarkImageControlProps } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantWatermarkSettings';
import { PrivateWorkspaceAgentSelectAssetDropdown } from '~/components/v2/components/workspace/workspace-agent-panel/components/select-asset/PrivateWorkspaceAgentSelectAssetDropdown';
import { type WorkspaceAgentSelectAssetValue } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { type PrivateWorkspaceAgentBoardAssetSearchFilters } from '~/components/v2/components/workspace/workspace-agent-panel/queries/getPrivateWorkspaceAgentBoardAssetSearchFilters';
import {
  isWorkspaceAgentWatermarkAssetMention,
  type WorkspaceAgentWatermarkImage,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentWatermark';

const WATERMARK_IMAGE_CLIP_FILTERS: PrivateWorkspaceAgentBoardAssetSearchFilters = {
  assetFileKind: { is: 'image' },
};

export type PrivateWorkspaceAgentGuidedVariantWatermarkImageControlProps =
  WorkspaceAgentGuidedVariantWatermarkImageControlProps;

const toWatermarkImage = (item: WorkspaceAgentSelectAssetValue): WorkspaceAgentWatermarkImage | null => {
  if (item.kind === 'contextSourceFieldValue') {
    return item;
  }

  if (isWorkspaceAgentWatermarkAssetMention(item)) {
    return item;
  }

  return null;
};

export const PrivateWorkspaceAgentGuidedVariantWatermarkImageControl = memo(
  ({ onSelect }: PrivateWorkspaceAgentGuidedVariantWatermarkImageControlProps) => {
    const handleSelect = useCallback(
      (item: WorkspaceAgentSelectAssetValue | null) => {
        if (!item) {
          onSelect(null);

          return;
        }

        const image = toWatermarkImage(item);

        if (!image) {
          return;
        }

        onSelect(image);
      },
      [onSelect],
    );

    return (
      <PrivateWorkspaceAgentSelectAssetDropdown
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_PICK"
        filters={WATERMARK_IMAGE_CLIP_FILTERS}
        onSelect={handleSelect}
      />
    );
  },
);

PrivateWorkspaceAgentGuidedVariantWatermarkImageControl.displayName =
  'PrivateWorkspaceAgentGuidedVariantWatermarkImageControl';
